import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Auffangnetz für Fehler beim Zeichnen.
 *
 * React wirft bei einem Fehler in einer Komponente den gesamten Baum weg — aus
 * einer einzelnen kaputten Karte wird eine weiße Seite, und die einzige Spur
 * steht in der Konsole des Browsers. Genau so ist ein neues Feld in der
 * Positionsansicht aufgeschlagen, während die API noch in der vorherigen Fassung
 * lief.
 *
 * Diese Grenze hält den Schaden örtlich: Der Rest der Oberfläche bleibt bedienbar,
 * und der Fehler steht auf dem Schirm statt nur im Protokoll.
 *
 * Als Klasse, weil React für `getDerivedStateFromError` keine Entsprechung mit
 * Haken anbietet — das ist die einzige Stelle im Projekt, an der das gilt.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Benennt den Bereich in der Meldung, etwa „Portfolio". */
  area: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* Der Stapelauszug der Komponenten sagt, welche Karte es war — er steht nicht
     * im Fehler selbst, sondern nur hier. */
    console.error(`[${this.props.area}]`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="error-boundary" role="alert">
        <strong>{this.props.area} konnte nicht dargestellt werden.</strong>
        <p className="muted small">{error.message}</p>
        <button type="button" onClick={() => this.setState({ error: null })}>
          Erneut versuchen
        </button>
      </div>
    );
  }
}
